import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, Trash2, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, fmtD, DOC_CATEGORIES } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

export default function TeamDocuments({ cid, onChanged }) {
  const [docs, setDocs] = useState(null);
  const [catF, setCatF] = useState("Tous");
  const [upOpen, setUpOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    api.get(`/clubs/${cid}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const filtered = (docs || []).filter((d) => catF === "Tous" || d.category === catF);

  const download = async (d) => {
    try {
      const r = await api.get(`/clubs/${cid}/documents/${d.id}/download`);
      const a = document.createElement("a");
      a.href = `data:${r.data.mime};base64,${r.data.data_b64}`;
      a.download = r.data.filename || d.label;
      a.click();
    } catch { toast.error(tfr("Téléchargement impossible")); }
  };
  const del = async () => {
    try {
      await api.delete(`/clubs/${cid}/documents/${confirmDel.id}`);
      toast.success(tfr("Document supprimé")); setConfirmDel(null); load(); onChanged();
    } catch { toast.error(tfr("Suppression impossible")); }
  };

  if (!docs) return <div className="h-48 rounded-2xl bg-muted/60 animate-pulse" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {["Tous", ...DOC_CATEGORIES].map((c) => (
          <button key={c} onClick={() => setCatF(c)} data-testid={`doc-filter-${c}`}
            className={cn("h-9 px-3 rounded-full border text-xs transition-colors",
              catF === c ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
            {c}
          </button>
        ))}
        <Button size="sm" onClick={() => setUpOpen(true)} className="h-9 gap-1 bg-athlynx text-white hover:bg-athlynx/90 ml-auto" data-testid="doc-add">
          <Plus className="h-3.5 w-3.5" /> Ajouter un document
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Empty text={docs.length ? tfr("Aucun document dans cette catégorie.") : tfr("Aucun document lié à cette équipe.")}
          cta={docs.length ? null : tfr("Ajouter un document")} onClick={() => setUpOpen(true)} testid="doc-empty" />
      ) : (
        <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none divide-y divide-border/70" data-testid="doc-list">
          {filtered.map((d, i) => {
            const expDays = d.expires_at ? Math.floor((new Date(d.expires_at) - Date.now()) / 86400000) : null;
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`doc-row-${i}`}>
                <span className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{d.label}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[d.category, d.size ? `${Math.max(1, Math.round(d.size / 1024))} Ko` : null, fmtD(d.created_at), d.player?.name].filter(Boolean).join(" · ")}
                  </p>
                </div>
                {expDays != null && (
                  <span className={cn("text-[10px] rounded-full border px-2 py-0.5 shrink-0",
                    expDays < 0 ? "border-rose-500/40 text-rose-600 bg-rose-500/10 dark:text-rose-300"
                      : expDays <= 30 ? "border-amber-500/40 text-amber-700 bg-amber-500/10 dark:text-amber-300"
                        : "border-border text-muted-foreground bg-secondary/50")}>
                    {expDays < 0 ? tfr("Expiré") : `${tfr("Expire dans")} ${expDays} ${tfr("j")}`}
                  </span>
                )}
                <div className="flex gap-1.5 ml-auto">
                  <button onClick={() => download(d)} aria-label={`Télécharger ${d.label}`} data-testid={`doc-download-${i}`}
                    className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Download className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setConfirmDel(d)} aria-label={`Supprimer ${d.label}`}
                    className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {upOpen && <UploadDialog cid={cid} onClose={() => setUpOpen(false)} onSaved={() => { setUpOpen(false); load(); onChanged(); }} />}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">{tfr("Supprimer ce document ?")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{tfr("Cette action est définitive.")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>{tfr("Annuler")}</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={del} data-testid="doc-del-confirm">{tfr("Supprimer")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadDialog({ cid, onClose, onSaved }) {
  const [form, setForm] = useState({ label: "", category: "Autre", expires_at: "", player_id: "" });
  const [file, setFile] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get(`/clubs/${cid}/athletes`).then((r) => setAthletes(r.data || [])); }, [cid]);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (10 Mo max)"); return; }
    setFile(f);
    if (!form.label) set("label", f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file || !form.label.trim() || busy) return;
    setBusy(true);
    try {
      const data_b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/clubs/${cid}/documents`, {
        label: form.label, category: form.category, expires_at: form.expires_at || null,
        player_id: form.player_id || null, filename: file.name, mime: file.type || "application/octet-stream", data_b64,
      });
      toast.success(tfr("Document ajouté"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle className="text-base">{tfr("Ajouter un document")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{tfr("Fichier")}</Label>
            <Input type="file" onChange={(e) => onFile(e.target.files?.[0])} className="h-10 mt-1 pt-2" data-testid="doc-file" />
          </div>
          <div><Label className="text-xs">Nom</Label>
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder={tfr("Nom du document")} className="h-10 mt-1" data-testid="doc-label" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{tfr("Catégorie")}</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-10 mt-1 text-xs" data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Expiration (optionnel)</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} className="h-10 mt-1" /></div>
          </div>
          <div><Label className="text-xs">{tfr("Athlète lié (optionnel)")}</Label>
            <Select value={form.player_id || "none"} onValueChange={(v) => set("player_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-10 mt-1 text-xs"><SelectValue placeholder={tfr("Aucun")} /></SelectTrigger>
              <SelectContent><SelectItem value="none">{tfr("Aucun")}</SelectItem>
                {athletes.map((l) => <SelectItem key={l.player_id} value={l.player_id}>{l.player?.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <Button onClick={submit} disabled={!file || !form.label.trim() || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="doc-submit">
          {busy ? "Envoi…" : "Ajouter le document"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
